import { useEffect, useRef, useState } from "react";
import { Plus, Undo2, X } from "lucide-react";
import { NewClassInput, NewPersonForm } from "./NewPersonForm";

const KINDS = [["ind", "Individual"], ["sib", "Sibling"], ["grad", "Graduation"]];

function useDismiss(ref, onClose) {
  useEffect(() => {
    const away = (e) => ref.current && !ref.current.contains(e.target) && onClose();
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [ref, onClose]);
}

export function KindPicker({ at, count, onPick, onClose }) {
  const ref = useRef(null);
  useDismiss(ref, onClose);
  return (
    <div ref={ref} style={{ left: Math.min(at.x, window.innerWidth - 260), top: Math.min(at.y, window.innerHeight - 120) }}
      className="fixed z-[60] w-60 bg-slate-900 border border-emerald-600/60 rounded-md shadow-2xl p-2.5 space-y-2" data-testid="drop-kind-picker">
      <p className="text-[11px] text-slate-200 truncate" data-testid="drop-kind-title">
        {count} photo{count > 1 ? "s" : ""} → <span className="text-emerald-200 font-bold">{at.p.first} {at.p.last}</span>
      </p>
      <div className="flex gap-1">
        {KINDS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => onPick(k)} data-testid={`drop-kind-${k}`}
            className="flex-1 h-7 rounded-sm border border-slate-700 text-[10px] font-bold uppercase tracking-widest text-slate-200 hover:bg-emerald-500 hover:text-slate-950 transition-colors">
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AssignMenu({ at, count, classes: known, onAssign, onAddPerson, onBack, onClose }) {
  const [cls, setCls] = useState(0);
  const [kind, setKind] = useState("ind");
  const [extra, setExtra] = useState([]);
  const [newClass, setNewClass] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useDismiss(ref, onClose);
  const classes = [...known, ...extra.map((name) => ({ name, children: [], staff: [] }))];
  const c = classes[cls];
  const addClass = (name) => {
    const at = classes.findIndex((k) => k.name.toLowerCase() === name.toLowerCase());
    if (at < 0) setExtra((x) => [...x, name]);
    setCls(at < 0 ? classes.length : at);
    setNewClass(false);
    setAdding(true);
  };
  const savePerson = async (p) => {
    setBusy(true);
    await onAddPerson({ ...p, room: c.name }, kind);
    setBusy(false);
  };
  const people = c ? [...c.children, ...c.staff] : [];
  const left = Math.min(at.x, window.innerWidth - 300);
  const top = Math.min(at.y, window.innerHeight - 440);
  return (
    <div ref={ref} style={{ left, top }} data-testid="assign-menu"
      className="fixed z-[60] w-72 bg-slate-900 border border-slate-700 rounded-md shadow-2xl p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-widest text-amber-200" data-testid="assign-menu-count">{count} photo{count > 1 ? "s" : ""} selected</p>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-100" data-testid="assign-menu-close"><X className="h-4 w-4" /></button>
      </div>
      {newClass ? <NewClassInput onAdd={addClass} onCancel={() => setNewClass(false)} /> : (
        <div className="flex gap-1">
          <select value={cls} onChange={(e) => setCls(Number(e.target.value))} data-testid="assign-class-select"
            className="flex-1 min-w-0 h-8 px-2 rounded-sm bg-slate-950 border border-slate-700 text-[12px] text-slate-100 focus:border-amber-400 outline-none">
            {classes.map((k, i) => <option key={k.name} value={i}>{k.name}</option>)}
          </select>
          <button type="button" onClick={() => setNewClass(true)} title="Add a class" data-testid="assign-add-class-btn"
            className="h-8 px-2 inline-flex items-center gap-1 rounded-sm border border-slate-700 text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:bg-slate-800">
            <Plus className="h-3.5 w-3.5" />Class
          </button>
        </div>
      )}
      <div className="flex gap-1" data-testid="assign-kind-group">
        {KINDS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`assign-kind-${k}`}
            className={`flex-1 h-7 rounded-sm text-[10px] font-bold uppercase tracking-widest transition-colors ${kind === k ? "bg-amber-400 text-slate-950" : "border border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>
      {people.length > 0 && <div className="max-h-56 overflow-y-auto border border-slate-800 rounded-sm" data-testid="assign-child-list">
        {people.map((p) => (
          <button key={p.idx} type="button" onClick={() => onAssign(p.idx, kind)} data-testid={`assign-child-${p.idx}`}
            className="w-full text-left px-2.5 h-8 text-[12px] text-slate-200 hover:bg-amber-400/15 hover:text-amber-100 transition-colors flex justify-between">
            <span className="truncate">{p.first} {p.last}</span>
            {p.is_staff && <span className="text-[9px] uppercase text-slate-500">staff</span>}
          </button>
        ))}
      </div>}
      {c && (adding || !people.length) ? <NewPersonForm key={c.name} room={c.name} busy={busy} onSave={savePerson} /> : c && (
        <button type="button" onClick={() => setAdding(true)} data-testid="assign-add-person-btn"
          className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-sm border border-dashed border-slate-700 text-[11px] text-slate-300 hover:bg-slate-800">
          <Plus className="h-3.5 w-3.5" />Add person to {c.name}
        </button>
      )}
      <button type="button" onClick={onBack} data-testid="assign-back-to-review"
        className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-sm border border-slate-700 text-[11px] text-slate-300 hover:bg-slate-800">
        <Undo2 className="h-3.5 w-3.5" />Send back to review
      </button>
    </div>
  );
}
